import { Dispatch, SetStateAction, useCallback, useMemo, useState } from "react";
import type { FeatureCollection, MultiPolygon, Polygon } from "geojson";

import { ParkingPaymentModeType, PaymentZoneArea, SavePaymentZoneAreaPayload } from "../payment-zone-area.type";
import {
  PAYMENT_ZONE_COLORS,
  createDraftState,
  multiPolygonToPolygons,
  polygonsToMultiPolygon,
} from "../parking-payment.const";
import { toast } from "sonner";

export type DraftState = {
  mode: "create" | "edit";
  status: "form" | "drawing";
  name: string;
  color: [number, number, number, number];
  baseFeatureCount: number;
  zoneId?: string;
  originalGeometry?: MultiPolygon;
  originalZone?: PaymentZoneArea;
  markedForDeletion?: boolean;
};

type UsePaymentZoneCreationOptions = {
  appMode: ParkingPaymentModeType;
  changeAppMode: Dispatch<SetStateAction<ParkingPaymentModeType>>;
  paymentZoneAreaEditionData: FeatureCollection;
  setPaymentAreaZoneEditionData: Dispatch<SetStateAction<FeatureCollection>>;
  setPaymentZoneAreaDetail: (zone: PaymentZoneArea | undefined) => void;
  savePaymentZoneAreaProperties: (payload: SavePaymentZoneAreaPayload) => Promise<void>;
  deletePaymentZoneArea: (zoneId: string) => Promise<void>;
  setDraftColor: Dispatch<SetStateAction<[number, number, number, number] | undefined>>;
};

export const usePaymentZoneCreation = ({
  appMode,
  changeAppMode,
  paymentZoneAreaEditionData,
  setPaymentAreaZoneEditionData,
  setPaymentZoneAreaDetail,
  savePaymentZoneAreaProperties,
  deletePaymentZoneArea,
  setDraftColor,
}: UsePaymentZoneCreationOptions) => {
  const [draft, setDraft] = useState<DraftState | null>(null);
  const [isSavingCreation, setIsSavingCreation] = useState(false);

  const featureCount = paymentZoneAreaEditionData.features.length;

  // ============ Helper functions ============

  const showToast = useCallback((type: "success" | "error", message: string) => {
    toast[type](message, { style: { color: type === "success" ? "#228B22" : "#FF0000" } });
  }, []);

  const removeNewFeatures = useCallback(
    (baseCount: number) => {
      setPaymentAreaZoneEditionData((current) => ({
        ...current,
        features: current.features.slice(0, baseCount),
      }));
    },
    [setPaymentAreaZoneEditionData],
  );

  const cleanupDraft = useCallback(() => {
    setDraft(null);
    setDraftColor(undefined);
    setPaymentZoneAreaDetail(undefined);
    changeAppMode(ParkingPaymentModeType.ManagePaymentZoneArea);
  }, [changeAppMode, setDraftColor, setPaymentZoneAreaDetail]);

  const changeAppModeIfNeeded = useCallback(
    (targetMode: ParkingPaymentModeType) => {
      if (appMode !== targetMode) {
        changeAppMode(targetMode);
      }
    },
    [appMode, changeAppMode],
  );

  // ============ Main actions ============

  const startCreation = useCallback(() => {
    setDraft(createDraftState(featureCount, "create"));
    setDraftColor(undefined);
    setPaymentZoneAreaDetail(undefined);
    changeAppModeIfNeeded(ParkingPaymentModeType.ManagePaymentZoneArea);
  }, [featureCount, setDraftColor, setPaymentZoneAreaDetail, changeAppModeIfNeeded]);

  const startEdition = useCallback(
    (zone: PaymentZoneArea) => {
      if (!zone?.id) return;

      const zoneColor = zone.color ?? zone.properties?.color ?? PAYMENT_ZONE_COLORS[0]?.rgba;
      const zoneName = zone.properties?.name ?? zone.name ?? "";

      setDraft(
        createDraftState(featureCount, "edit", {
          name: zoneName,
          color: zoneColor,
          zoneId: zone.id?.toString(),
          originalGeometry: zone.geometry as unknown as MultiPolygon,
          originalZone: zone,
        }),
      );
      setDraftColor(undefined);
      setPaymentZoneAreaDetail(zone);
      changeAppModeIfNeeded(ParkingPaymentModeType.EditPaymentZoneArea);
    },
    [featureCount, setDraftColor, setPaymentZoneAreaDetail, changeAppModeIfNeeded],
  );

  const beginDrawing = useCallback(() => {
    setDraft((prev) => {
      if (!prev) return prev;
      const next = { ...prev, status: "drawing" as const, baseFeatureCount: featureCount };
      setDraftColor(next.color);
      return next;
    });
    changeAppMode(ParkingPaymentModeType.AddPaymentZoneArea);
    if (draft?.mode === "create") {
      setPaymentZoneAreaDetail(undefined);
    }
  }, [changeAppMode, draft?.mode, featureCount, setDraftColor, setPaymentZoneAreaDetail]);

  const importPolygonsToDraft = useCallback(
    (polygons: Polygon[]) => {
      if (!draft || polygons.length === 0) return;

      const color = draft.color;
      const importedFeatures = polygons.map((geometry) => ({
        type: "Feature" as const,
        geometry,
        properties: { color },
      }));

      setDraft((prev) => {
        if (!prev) return prev;
        // Preserve the base while drawing so already-added, unsaved features stay in the save slice.
        const baseFeatureCount = prev.status === "drawing" ? prev.baseFeatureCount : featureCount;
        return { ...prev, status: "drawing" as const, baseFeatureCount };
      });
      setDraftColor(color);
      setPaymentAreaZoneEditionData((current) => ({
        ...current,
        features: [...current.features, ...importedFeatures],
      }));
      changeAppMode(ParkingPaymentModeType.AddPaymentZoneArea);
      if (draft.mode === "create") {
        setPaymentZoneAreaDetail(undefined);
      }
    },
    [draft, featureCount, changeAppMode, setDraftColor, setPaymentAreaZoneEditionData, setPaymentZoneAreaDetail],
  );

  const updateDraft = useCallback(
    (updates: Partial<Pick<DraftState, "name" | "color">>) => {
      setDraft((prev) => {
        if (!prev) return prev;

        const next = { ...prev, ...updates };

        if (updates.color && prev.status === "drawing") {
          setDraftColor(updates.color);
        }

        if (prev.mode === "edit" && prev.zoneId && updates.color) {
          setPaymentAreaZoneEditionData((current) => ({
            ...current,
            features: current.features.map((f) =>
              f?.id?.toString() === prev.zoneId
                ? { ...f, properties: { ...(f.properties ?? {}), color: updates.color } }
                : f,
            ),
          }));
        }

        return next;
      });
    },
    [setDraftColor, setPaymentAreaZoneEditionData],
  );

  const cancelCreation = useCallback(() => {
    if (!draft) return;

    const { mode, status, baseFeatureCount, zoneId, originalGeometry, originalZone } = draft;

    if (mode === "edit" && status === "drawing") {
      removeNewFeatures(baseFeatureCount);
      setDraft((prev) => (prev ? { ...prev, status: "form" as const } : null));
      setDraftColor(undefined);
      changeAppMode(ParkingPaymentModeType.EditPaymentZoneArea);
      return;
    }

    if (mode === "create") {
      removeNewFeatures(baseFeatureCount);
    }

    if (mode === "edit" && zoneId) {
      const featureExists = paymentZoneAreaEditionData.features.some((f) => f?.id?.toString() === zoneId);

      setPaymentAreaZoneEditionData((current) => {
        if (!featureExists && originalZone) {
          return { ...current, features: [...current.features, originalZone] };
        }
        if (originalGeometry) {
          return {
            ...current,
            features: current.features.map((f) =>
              f?.id?.toString() === zoneId ? { ...f, geometry: originalGeometry } : f,
            ),
          };
        }
        return current;
      });
    }

    setDraft(null);
    setDraftColor(undefined);
    setPaymentZoneAreaDetail(undefined);
    if (
      appMode === ParkingPaymentModeType.AddPaymentZoneArea ||
      (appMode === ParkingPaymentModeType.EditPaymentZoneArea && mode === "edit")
    ) {
      changeAppMode(ParkingPaymentModeType.ManagePaymentZoneArea);
    }
  }, [
    draft,
    setDraftColor,
    setPaymentZoneAreaDetail,
    appMode,
    removeNewFeatures,
    paymentZoneAreaEditionData.features,
    setPaymentAreaZoneEditionData,
    changeAppMode,
  ]);

  const finishCreation = useCallback(async () => {
    if (draft?.mode !== "create" || draft.status !== "drawing") return;

    const draftFeatures = paymentZoneAreaEditionData.features.slice(draft.baseFeatureCount) as PaymentZoneArea[];

    if (!draftFeatures.length) return;

    const geometry = polygonsToMultiPolygon(draftFeatures.map((f) => f.geometry));

    setIsSavingCreation(true);
    try {
      await savePaymentZoneAreaProperties({
        properties: { name: draft.name.trim(), color: draft.color },
        geometry,
      });

      removeNewFeatures(draft.baseFeatureCount);
      cleanupDraft();
      showToast("success", "Zone créée avec succès !");
    } catch (error) {
      showToast("error", "Erreur lors de la création de la zone");
      return error;
    } finally {
      setIsSavingCreation(false);
    }
  }, [
    draft,
    paymentZoneAreaEditionData.features,
    savePaymentZoneAreaProperties,
    removeNewFeatures,
    cleanupDraft,
    showToast,
  ]);

  const submitEdition = useCallback(async () => {
    if (draft?.mode !== "edit") return;

    const name = draft.name.trim();
    if (!name) return;

    setIsSavingCreation(true);
    try {
      if (draft.markedForDeletion && draft.zoneId) {
        await deletePaymentZoneArea(draft.zoneId);
        cleanupDraft();
        showToast("success", "Zone supprimée avec succès !");
        return;
      }

      let geometry: MultiPolygon | undefined;

      // If in drawing mode, merge new polygons with existing geometry
      if (draft.status === "drawing") {
        const existingZone = paymentZoneAreaEditionData.features.find(
          (f) => f?.id?.toString() === draft.zoneId,
        ) as PaymentZoneArea;

        const newDraftFeatures = paymentZoneAreaEditionData.features.slice(draft.baseFeatureCount) as PaymentZoneArea[];

        if (existingZone?.geometry && newDraftFeatures.length > 0) {
          const existingPolygons = multiPolygonToPolygons(existingZone.geometry as unknown as MultiPolygon);
          const newPolygons = newDraftFeatures.map((f) => f.geometry as Polygon);
          geometry = polygonsToMultiPolygon([...existingPolygons, ...newPolygons]);
        }
      }

      await savePaymentZoneAreaProperties({
        properties: { name, color: draft.color },
        ...{ geometry },
        zoneId: draft.zoneId,
      });

      setPaymentAreaZoneEditionData((c) => ({
        ...c,
        features: c.features.slice(0, draft.baseFeatureCount).map((f) =>
          f?.id?.toString() === draft.zoneId
            ? {
                ...f,
                properties: {
                  ...(f.properties ?? {}),
                  name,
                  color: draft.color,
                },
              }
            : f,
        ),
      }));

      cleanupDraft();
      showToast("success", "Zone mise à jour avec succès !");
    } finally {
      setIsSavingCreation(false);
    }
  }, [
    draft,
    paymentZoneAreaEditionData,
    savePaymentZoneAreaProperties,
    deletePaymentZoneArea,
    setPaymentAreaZoneEditionData,
    cleanupDraft,
    showToast,
  ]);

  const draftPolygonCount = useMemo(() => {
    if (!draft) return 0;

    // En mode édition, calculer le total (existants + nouveaux)
    if (draft.mode === "edit" && draft.zoneId) {
      const existingZone = paymentZoneAreaEditionData.features.find((f) => f?.id?.toString() === draft.zoneId) as
        | PaymentZoneArea
        | undefined;

      if (existingZone?.geometry) {
        const existingPolygonsCount =
          existingZone.geometry.type === "Polygon" ? 1 : existingZone.geometry.coordinates.length;

        if (draft.status === "drawing") {
          const newPolygonsCount = Math.max(0, featureCount - draft.baseFeatureCount);
          return existingPolygonsCount + newPolygonsCount;
        }

        return existingPolygonsCount;
      }
    }

    // En mode création, compter seulement les nouveaux
    if (draft.status === "drawing") {
      return Math.max(0, featureCount - draft.baseFeatureCount);
    }

    return 0;
  }, [draft, featureCount, paymentZoneAreaEditionData]);

  return {
    draft,
    isSavingCreation,
    draftPolygonCount,
    startCreation,
    startEdition,
    updateDraft,
    beginDrawing,
    importPolygonsToDraft,
    cancelCreation,
    finishCreation,
    submitEdition,
  };
};
